export interface Product {
  name: string
  slug: string
  description: string
  image: string
  images?: string[]
  pricePerYard: number
  specs?: {
    size?: string
    weightPerYard?: string
    compaction?: string
    coverage?: string
  }
  bestFor?: string[]
  proTip?: string
}

export interface Category {
  name: string
  description: string
  image: string
  products: Product[]
}

export const categories: Category[] = [
  {
    name: 'Base & Compaction',
    description:
      'Angular, crushed stone and recycled materials that lock together and compact into a solid, load-bearing surface. The go-to choices for driveways, parking pads, road bases, walkways, and any project that needs a stable foundation.',
    image:
      'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=800&h=400&fit=crop',
    products: [
      {
        name: '5/8" Minus',
        slug: '5-8-minus',
        description:
          'Our most popular driveway gravel. This finely crushed, angular stone includes a mix of particles down to dust size, which allows it to pack down tight and create a smooth, firm surface. Ideal for driveways, walkways, patio bases, and as a top layer over coarser base rock. Compacts beautifully with minimal maintenance.',
        image:
          'https://cdn.magicpatterns.com/uploads/7G9YZaEtyKVu5ab5dYbZxn/PXL_20260422_184432798.PORTRAIT~2.jpg',
        images: [
          'https://cdn.magicpatterns.com/uploads/7G9YZaEtyKVu5ab5dYbZxn/PXL_20260422_184432798.PORTRAIT~2.jpg',
          'https://cdn.magicpatterns.com/uploads/7QxRMTLFhKZH6A9aYsWUw1/PXL_20260422_184444516.PORTRAIT~2.jpg',
          'https://cdn.magicpatterns.com/uploads/uJHdEMdk8Ku5nMisWk411s/58minus.png',
        ],
        pricePerYard: 55,
        specs: {
          size: 'Up to 5/8" with fines',
          weightPerYard: '~2,800 lbs (1.4 tons)',
          compaction: 'Excellent',
          coverage: '1 yard covers ~100 sq ft at 3" deep',
        },
        bestFor: ['Driveways', 'Walkways', 'Patio Bases', 'Top Layer'],
        proTip:
          'For a new driveway, use 1.25" Minus as your base layer, then top with 2-3 inches of 5/8" Minus for a smooth finish.',
      },
      {
        name: '1.25" Minus',
        slug: '1-25-minus',
        description:
          'A coarser crushed rock with pieces up to 1.25 inches, blended with smaller fines for excellent compaction. This is your heavy-duty base layer — perfect for building up driveways, establishing road base, stabilizing parking areas, and providing structural support under concrete or pavers. Often used as the first layer before topping with 5/8" Minus.',
        image:
          'https://cdn.magicpatterns.com/uploads/9E85amw56n3D8ydgKmAG2Z/1_25_minus_gravel.jpg',
        images: [
          'https://cdn.magicpatterns.com/uploads/9E85amw56n3D8ydgKmAG2Z/1_25_minus_gravel.jpg',
          'https://cdn.magicpatterns.com/uploads/7VsC9jjSWLbwV6Us3bXNhz/inch_25_minus2.png',
        ],
        pricePerYard: 55,
        specs: {
          size: 'Up to 1.25" with fines',
          weightPerYard: '~2,800 lbs (1.4 tons)',
          compaction: 'Excellent (Structural)',
          coverage: '1 yard covers ~80 sq ft at 4" deep',
        },
        bestFor: [
          'Driveway Base',
          'Road Base',
          'Parking Pads',
          'Under Concrete',
        ],
        proTip:
          'Compact in 3-4 inch lifts (layers) using a vibratory plate compactor for maximum stability.',
      },
      {
        name: 'Walkway Gravel',
        slug: '3-8-minus-driveway',
        description:
          'A finer crushed rock with angular pieces up to 3/8 inch, blended with smaller fines for a tight, smooth finish. Compacts exceptionally well, making it a top choice for driveways, walkways, and patios where you want a clean, packed surface with minimal loose stone. Easy to spread and maintain.',
        image:
          'https://cdn.magicpatterns.com/uploads/7tucQVL97E7dTm2FTUp6bj/PXL_20260414_153527381.jpg',
        images: [
          'https://cdn.magicpatterns.com/uploads/7tucQVL97E7dTm2FTUp6bj/PXL_20260414_153527381.jpg',
          'https://cdn.magicpatterns.com/uploads/cGVEgK2FWiTt371km5oGeG/file_0000000030b471fd97f2b00e45cd8c0e.png',
        ],
        pricePerYard: 50,
        specs: {
          size: 'Up to 3/8" with fines',
          weightPerYard: '~2,800 lbs (1.4 tons)',
          compaction: 'Excellent (Smooth)',
          coverage: '1 yard covers ~160 sq ft at 2" deep',
        },
        bestFor: ['Walkways', 'Patios', 'Smooth Driveways', 'Bocce Courts'],
        proTip:
          "Because it packs so tightly, it is easier to walk on than larger gravels and won't shift underfoot.",
      },
      {
        name: 'Recycled Asphalt',
        slug: 'recycled-asphalt',
        description:
          'Milled and crushed asphalt from road and parking lot projects. The residual tar in the material causes it to re-bind over time, especially in warm weather, creating a semi-hard surface that resists erosion. Popular for rural driveways, farm roads, temporary construction access, and as a cost-effective alternative to fresh asphalt paving.',
        image:
          'https://cdn.magicpatterns.com/uploads/pTVDr44Wtojj8Anp2QQMDM/image.png',
        pricePerYard: 35,
        specs: {
          size: 'Varies (Milled)',
          weightPerYard: '~2,800 lbs (1.4 tons)',
          compaction: 'Excellent (Re-binds)',
          coverage: '1 yard covers ~100 sq ft at 3" deep',
        },
        bestFor: [
          'Rural Driveways',
          'Farm Roads',
          'Parking Areas',
          'Budget Projects',
        ],
        proTip:
          'Apply during warm summer months — the heat helps the residual tar soften and re-bind the material into a hard surface.',
      },
      {
        name: 'Recycled Concrete',
        slug: 'recycled-concrete',
        description:
          'Crushed, screened, and sized concrete reclaimed from local demolition projects. Compacts well and provides a stable, load-bearing base similar to virgin crushed rock. A smart, affordable choice for road base, driveway sub-base, backfill behind walls, and utility trench bedding. Keeps usable material out of the landfill while saving you money.',
        image:
          'https://cdn.magicpatterns.com/uploads/rAceDaqeD4rJw2Yc7jJZMJ/recycled_concrete_tacoma.png',
        pricePerYard: 35,
        specs: {
          size: 'Typically 1.25" Minus',
          weightPerYard: '~2,600 lbs (1.3 tons)',
          compaction: 'Excellent',
          coverage: '1 yard covers ~80 sq ft at 4" deep',
        },
        bestFor: ['Sub-base', 'Road Base', 'Backfill', 'Eco-friendly Projects'],
        proTip:
          'An excellent, budget-friendly alternative to 1.25" Minus for deep base layers that will be covered by a nicer top rock.',
      },
      {
        name: 'Fill Dirt',
        slug: 'fill-dirt',
        description:
          "Clean, screened fill dirt for non-growing applications. Use it to raise grades, backfill foundations, fill in holes and low spots, or build up areas before laying gravel or sod. This is subsoil — it won't grow a lawn on its own, but it's the most affordable way to move earth and establish proper drainage slopes on your property.",
        image:
          'https://cdn.magicpatterns.com/uploads/qxdinDCoHWVmb1tuCPfFVL/PXL_20260429_192945832.PORTRAIT~2.jpg',
        images: [
          'https://cdn.magicpatterns.com/uploads/qxdinDCoHWVmb1tuCPfFVL/PXL_20260429_192945832.PORTRAIT~2.jpg',
          'https://cdn.magicpatterns.com/uploads/mvue4Ss3PpCv2evpn9ouKp/fill_dirt_253_gravel.png',
          'https://cdn.magicpatterns.com/uploads/5hyxo1R83jMsQdPWWHeLZn/PXL_20260429_192914551~3.jpg',
        ],
        pricePerYard: 20,
        specs: {
          size: 'Screened Subsoil',
          weightPerYard: '~2,600 lbs (1.3 tons)',
          compaction: 'Good',
          coverage: '1 yard covers ~80 sq ft at 4" deep',
        },
        bestFor: ['Raising Grade', 'Backfilling', 'Leveling', 'Subgrade'],
        proTip:
          'If you plan to plant over fill dirt, leave the top 4-6 inches for 3-Way Topsoil to ensure plants have nutrients to grow.',
      },
      {
        name: 'Builders Sand',
        slug: 'builders-sand',
        description:
          'Coarse, washed sand used in construction and landscaping. Essential for mixing concrete and mortar, leveling paver bases, filling horseshoe pits, and creating sand-set patios. Also works well as pipe bedding for utility trenches and as a leveling layer under above-ground pools. Clean and consistent grain size for reliable results.',
        image:
          'https://cdn.magicpatterns.com/uploads/t2AoqCyGNHu1JXzJkEpNao/utility_sand.jpg',
        pricePerYard: 60,
        specs: {
          size: 'Coarse Sand',
          weightPerYard: '~2,800 lbs (1.4 tons)',
          compaction: 'Good (when wet)',
          coverage: '1 yard covers ~324 sq ft at 1" deep',
        },
        bestFor: [
          'Paver Base',
          'Concrete Mixing',
          'Pipe Bedding',
          'Pool Leveling',
        ],
        proTip:
          'When setting pavers, use exactly 1 inch of sand over a compacted gravel base. Do not use sand to fix major dips in the gravel base.',
      },
      {
        name: 'Gravel Borrow',
        slug: 'type-17-gravel-borrow',
        description:
          'A naturally occurring pit-run gravel blend of sand, silt, and rounded stones up to 3 inches. Meets WSDOT Type 17 specifications for general fill and grading. Compacts well and is commonly used for backfill, raising grades, building up subgrade for driveways and roads, and general earthwork. An affordable, versatile material when you need bulk volume without the cost of crushed rock.',
        image:
          'https://cdn.magicpatterns.com/uploads/xdtEuDCxbSMgpE7Ec14LEF/type17_gravel_burrow.jpg',
        images: [
          'https://cdn.magicpatterns.com/uploads/xdtEuDCxbSMgpE7Ec14LEF/type17_gravel_burrow.jpg',
          'https://cdn.magicpatterns.com/uploads/jkHCfQtpWAphndEUJjpg4X/type17.jpg',
        ],
        pricePerYard: 40,
        specs: {
          size: 'Up to 3" mixed with sand/silt',
          weightPerYard: '~2,800 lbs (1.4 tons)',
          compaction: 'Excellent',
          coverage: '1 yard covers ~80 sq ft at 4" deep',
        },
        bestFor: ['Bulk Fill', 'Subgrade', 'Backfill', 'Earthwork'],
        proTip:
          'The most cost-effective way to build up a driveway subgrade before capping it with crushed rock.',
      },
    ],
  },
  {
    name: 'Clean Drain Rock',
    description:
      'Washed stone with no fines or dust — water flows freely through the gaps. The right choice for French drains, curtain drains, septic fields, retaining wall backfill, and any project where drainage performance is critical.',
    image:
      'https://images.unsplash.com/photo-1615811361523-6bd03d7748e7?w=800&h=400&fit=crop',
    products: [
      {
        name: '5/8" Clean',
        slug: '5-8-clear',
        description:
          'Clean, washed crushed stone with no fines or dust — just angular 5/8" pieces. Because there are no fine particles to clog the gaps, water flows freely through it, making this the preferred choice for French drains, curtain drains, and drainage trenches. Also works well as pipe bedding and behind retaining walls where water management is critical.',
        image:
          'https://cdn.magicpatterns.com/uploads/96U9gsJQpdtAbH8LKxzSbp/image.png',
        images: [
          'https://cdn.magicpatterns.com/uploads/96U9gsJQpdtAbH8LKxzSbp/image.png',
          'https://cdn.magicpatterns.com/uploads/bXdm32kexBvq2fSGoYeg5W/58clean.png',
        ],
        pricePerYard: 70,
        specs: {
          size: '5/8" (No Fines)',
          weightPerYard: '~2,700 lbs (1.35 tons)',
          compaction: 'Poor (Designed to drain)',
          coverage: '1 yard covers ~100 sq ft at 3" deep',
        },
        bestFor: [
          'French Drains',
          'Pipe Bedding',
          'Retaining Walls',
          'Curtain Drains',
        ],
        proTip:
          'Always wrap your drainage trench in non-woven geotextile fabric to prevent soil from clogging the rock over time.',
      },
      {
        name: '1.25" Clean',
        slug: '1-25-clear',
        description:
          'Larger washed angular stone, screened to remove all fines. The bigger gaps between stones allow for maximum water flow, making this ideal for heavy-duty drainage applications, septic drain fields, retaining wall backfill, and large-scale stormwater management. A workhorse material for any project where moving water away fast is the priority.',
        image:
          'https://cdn.magicpatterns.com/uploads/wwAR73RWBJ4Vnm85vU5YtU/image.png',
        images: [
          'https://cdn.magicpatterns.com/uploads/wwAR73RWBJ4Vnm85vU5YtU/image.png',
          'https://cdn.magicpatterns.com/uploads/iyKcHiQf7jUMcnbtPH51fD/125.png',
        ],
        pricePerYard: 62,
        specs: {
          size: '1.25" (No Fines)',
          weightPerYard: '~2,700 lbs (1.35 tons)',
          compaction: 'Poor (Designed to drain)',
          coverage: '1 yard covers ~80 sq ft at 4" deep',
        },
        bestFor: [
          'Heavy Drainage',
          'Septic Fields',
          'Retaining Wall Backfill',
          'Stormwater',
        ],
        proTip:
          'The larger voids in 1.25" Clean move water faster than 5/8" Clean, making it better for areas with heavy water flow.',
      },
      {
        name: '7/8" Drain Rock',
        slug: '7-8-drain-rock',
        description:
          'Clean, rounded rock sized at approximately 7/8 inch — the standard spec for residential French drains throughout the Puget Sound region. Washed free of fines so water passes through easily. Wrap it in filter fabric around your perforated pipe for a textbook drainage installation. Also commonly used for septic systems and as a base layer under decks.',
        image:
          'https://cdn.magicpatterns.com/uploads/rjg9Ff86Mr2rsdPCf5fYSM/drain_rock_small.png',
        images: [
          'https://cdn.magicpatterns.com/uploads/rjg9Ff86Mr2rsdPCf5fYSM/drain_rock_small.png',
          'https://cdn.magicpatterns.com/uploads/4CMhgoaosJRQ9X87j7Wdzf/7_8_drain_rock_delivery.png',
        ],
        pricePerYard: 62,
        specs: {
          size: '7/8" Rounded',
          weightPerYard: '~2,700 lbs (1.35 tons)',
          compaction: 'Poor (Designed to drain)',
          coverage: '1 yard covers ~100 sq ft at 3" deep',
        },
        bestFor: ['Residential French Drains', 'Under Decks', 'Septic Systems'],
        proTip:
          'Rounded rock is easier to shovel and work with by hand in tight trenches compared to angular crushed rock.',
      },
      {
        name: '1.5" Drain Rock',
        slug: '1-5-drain-rock',
        description:
          'Larger rounded drain rock for projects that need to move serious water. The bigger stone size creates larger voids for maximum flow capacity, making it the right call for commercial drainage, large septic drain fields, retaining wall weep systems, and anywhere you need heavy-duty water management. Clean-washed with no fines to restrict flow.',
        image:
          'https://cdn.magicpatterns.com/uploads/me3fE9s81YnndVtj12ocF9/drain_rock_large_253gravel.png',
        images: [
          'https://cdn.magicpatterns.com/uploads/me3fE9s81YnndVtj12ocF9/drain_rock_large_253gravel.png',
          'https://cdn.magicpatterns.com/uploads/sofXYMduQmUFzGgj6FJJFX/inch_quarter_drain_rock.png',
        ],
        pricePerYard: 70,
        specs: {
          size: '1.5" Rounded',
          weightPerYard: '~2,700 lbs (1.35 tons)',
          compaction: 'Poor (Designed to drain)',
          coverage: '1 yard covers ~80 sq ft at 4" deep',
        },
        bestFor: [
          'Commercial Drainage',
          'Large Septic Fields',
          'Heavy Water Flow',
        ],
        proTip:
          'Use for deep trenches or areas where you expect a high volume of water during heavy PNW rainstorms.',
      },
      {
        name: 'Pea Gravel',
        slug: 'pea-gravel',
        description:
          'Small, smooth, naturally rounded stones roughly the size of a pea (about 3/8"). Available in a mix of earth tones — tans, grays, whites, and browns. A versatile favorite for garden pathways, play areas, dog runs, patio fill between pavers, and decorative ground cover. Drains well and feels comfortable underfoot. Not recommended for driveways as it doesn\'t compact.',
        image:
          'https://cdn.magicpatterns.com/uploads/m7DBNtRmDZcJZBq5Nh3We2/pea_gravel_253.jpg',
        pricePerYard: 55,
        specs: {
          size: '~3/8" Rounded',
          weightPerYard: '~2,900 lbs (1.45 tons)',
          compaction: 'Poor (Loose surface)',
          coverage: '1 yard covers ~160 sq ft at 2" deep',
        },
        bestFor: ['Pathways', 'Dog Runs', 'Play Areas', 'Between Pavers'],
        proTip:
          "Because pea gravel rolls and doesn't compact, always use edging (like wood, stone, or metal) to keep it contained.",
      },
    ],
  },
  {
    name: 'Soil, Mulch & Org.',
    description:
      "Premium soil blends, bark mulch, and fill materials for growing, grading, and finishing. Whether you're starting a new lawn, amending garden beds, or giving landscape areas a clean, maintained look.",
    image:
      'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&h=400&fit=crop',
    products: [
      {
        name: '3-Way Topsoil',
        slug: '3-way-topsoil',
        description:
          'Our premium growing blend — a carefully balanced mix of screened topsoil, aged compost, and coarse sand. The topsoil provides structure, the compost delivers nutrients and beneficial microbes, and the sand ensures proper drainage. Ideal for establishing new lawns, filling raised garden beds, top-dressing existing turf, and any planting project where you want things to thrive from day one.',
        image:
          'https://cdn.magicpatterns.com/uploads/8yZLn1ArqywEa2vWNptiBJ/3way_topsoil_253gravel.jpg',
        images: [
          'https://cdn.magicpatterns.com/uploads/8yZLn1ArqywEa2vWNptiBJ/3way_topsoil_253gravel.jpg',
          'https://cdn.magicpatterns.com/uploads/2aMuTuHRRFN73t56LgdPvq/3way_soil.png',
        ],
        pricePerYard: 45,
        specs: {
          size: 'Screened Blend',
          weightPerYard: '~2,200 lbs (1.1 tons)',
          compaction: 'Light',
          coverage: '1 yard covers ~100 sq ft at 3" deep',
        },
        bestFor: ['New Lawns', 'Raised Beds', 'Planting', 'Top-dressing'],
        proTip:
          'For new lawns, spread 3-4 inches of 3-Way Topsoil over your subgrade before seeding or laying sod.',
      },
      {
        name: '50/50 Mix',
        slug: '50-50-mix',
        description:
          'A rich 50/50 blend of screened topsoil and aged compost, packed with organic matter and nutrients. This is the go-to mix for amending heavy clay soils common in the Puget Sound area, revitalizing tired garden beds, and boosting soil health in vegetable gardens and flower beds. Lighter and more nutrient-dense than straight topsoil.',
        image:
          'https://cdn.magicpatterns.com/uploads/9V5jP2dD5DeuoChaZYeoHk/5050mix_253gravel.jpg',
        images: [
          'https://cdn.magicpatterns.com/uploads/9V5jP2dD5DeuoChaZYeoHk/5050mix_253gravel.jpg',
          'https://cdn.magicpatterns.com/uploads/2WbQ2Ev6zgb1V371W4K46u/50_50_mix_topsoil_tacoma.png',
        ],
        pricePerYard: 52,
        specs: {
          size: 'Screened Blend',
          weightPerYard: '~2,200 lbs (1.1 tons)',
          compaction: 'Light',
          coverage: '1 yard covers ~100 sq ft at 3" deep',
        },
        bestFor: [
          'Amending Clay Soil',
          'Vegetable Gardens',
          'Flower Beds',
          'Revitalizing Soil',
        ],
        proTip:
          'Till 2-3 inches of 50/50 Mix into the top 6 inches of existing heavy clay soil to dramatically improve drainage and fertility.',
      },
      {
        name: 'Compost',
        slug: 'compost',
        description:
          'Rich, fully aged organic compost made from locally sourced yard waste and food scraps. Dark, crumbly, and packed with beneficial microbes and nutrients. Use it to amend existing soil, top-dress lawns, enrich raised beds, and boost the health of vegetable and flower gardens. Meets USCC standards for mature compost — no odor, no weed seeds, just pure soil food.',
        image:
          'https://cdn.magicpatterns.com/uploads/c1LwK73tKZyqZq5a6urRsA/compost_253gravel.png',
        pricePerYard: 60,
        specs: {
          size: 'Fine Organic Matter',
          weightPerYard: '~1,200 lbs (0.6 tons)',
          compaction: 'Very Light',
          coverage: '1 yard covers ~160 sq ft at 2" deep',
        },
        bestFor: [
          'Soil Amendment',
          'Top-dressing Lawns',
          'Mulching Gardens',
          'Nutrient Boost',
        ],
        proTip:
          'Top-dress your lawn with 1/4 to 1/2 inch of compost after aerating in the fall to naturally fertilize and improve soil structure.',
      },
      {
        name: 'Fine Bark',
        slug: 'fine-bark',
        description:
          'Finely shredded bark mulch with a smooth, uniform texture. The small pieces create a dense, tidy layer that hugs the soil closely, making it ideal for detailed garden beds, flower borders, and areas where a polished, manicured look is desired. Excellent moisture retention and weed suppression in a refined package.',
        image:
          'https://cdn.magicpatterns.com/uploads/jwh8xDZWrcPmgQp9pamYae/Fine-Bark-253gravel.jpg',
        pricePerYard: 45,
        specs: {
          size: 'Fine Shredded',
          weightPerYard: '~900 lbs (0.45 tons)',
          compaction: 'Light',
          coverage: '1 yard covers ~160 sq ft at 2" deep',
        },
        bestFor: [
          'Detailed Beds',
          'Flower Borders',
          'Manicured Look',
          'Moisture Retention',
        ],
        proTip:
          'Fine bark breaks down faster than coarser barks, feeding the soil more quickly but requiring more frequent top-ups.',
      },
      {
        name: 'Medium Bark',
        slug: 'medium-bark',
        description:
          'The most popular bark mulch in the Pacific Northwest. Medium-sized shredded bark pieces (roughly 1–3 inches) that lay flat, knit together well, and resist washing away on slopes. Perfect for flower beds, garden borders, around trees and shrubs, and anywhere you want a tidy, natural look. Breaks down slowly to feed the soil underneath over time.',
        image:
          'https://cdn.magicpatterns.com/uploads/uXAWFwyecXMMiXVD3PBG8P/medium_beauty_bark_253gravel.jpg',
        images: [
          'https://cdn.magicpatterns.com/uploads/uXAWFwyecXMMiXVD3PBG8P/medium_beauty_bark_253gravel.jpg',
          'https://cdn.magicpatterns.com/uploads/3bcz4ffHhDoYURGgMbiJcq/medium_bark.png',
        ],
        pricePerYard: 45,
        specs: {
          size: '1" - 3" Shredded',
          weightPerYard: '~900 lbs (0.45 tons)',
          compaction: 'Light',
          coverage: '1 yard covers ~160 sq ft at 2" deep',
        },
        bestFor: [
          'General Landscaping',
          'Slopes',
          'Around Trees',
          'Weed Suppression',
        ],
        proTip:
          'Apply 2-3 inches deep for optimal weed suppression and moisture retention. Keep bark a few inches away from tree trunks to prevent rot.',
      },
      {
        name: 'Coarse Bark',
        slug: 'coarse-bark',
        description:
          'Chunky, long-lasting bark nuggets (3–5 inches) that hold up well in high-traffic areas and exposed locations. The larger pieces decompose more slowly than medium bark, meaning fewer top-ups and lower long-term cost. Excellent for large landscape beds, commercial properties, hillside erosion control, and anywhere you want bold texture with maximum weed suppression.',
        image:
          'https://cdn.magicpatterns.com/uploads/wtRPmXAqzBUairA8ZDLYNy/coarse_bark_tacoma_delivery.png',
        pricePerYard: 45,
        specs: {
          size: '3" - 5" Nuggets',
          weightPerYard: '~900 lbs (0.45 tons)',
          compaction: 'Light',
          coverage: '1 yard covers ~100 sq ft at 3" deep',
        },
        bestFor: [
          'Large Beds',
          'Commercial Properties',
          'Windy Areas',
          'Long-lasting Mulch',
        ],
        proTip:
          'The best choice for windy areas or steep slopes, as the heavy, chunky pieces are less likely to blow or wash away.',
      },
    ],
  },
  {
    name: 'Landscape & Decor',
    description:
      'Naturally rounded, smooth stones and accent rock for landscaping projects that need a premium, eye-catching finish. Perfect for garden beds, dry creek beds, water features, and modern xeriscaping.',
    image:
      'https://images.unsplash.com/photo-1516808468759-390b1e428c0c?w=800&h=400&fit=crop',
    products: [
      {
        name: 'Streambed Cobble',
        slug: 'streambed-cobble',
        description:
          'Naturally rounded, water-tumbled cobblestones in a mix of sizes and earthy tones — grays, tans, and browns. Sourced from regional riverbeds, these stones bring a rugged, natural aesthetic to dry creek beds, drainage swales, water features, and landscape borders. Larger than pea gravel but smoother than quarry rock, streambed cobble bridges the gap between decorative and functional. Excellent for erosion control on slopes and as a natural-looking ground cover that requires zero maintenance.',
        image:
          'https://cdn.magicpatterns.com/uploads/5onxHUcUJws2dWn9MZKgbZ/PXL_20260413_181445485~2.jpg',
        images: [
          'https://cdn.magicpatterns.com/uploads/5onxHUcUJws2dWn9MZKgbZ/PXL_20260413_181445485~2.jpg',
          'https://cdn.magicpatterns.com/uploads/2NTKhJCUSWL4NnY8ELthNB/PXL_20260413_181442329.jpg',
        ],
        pricePerYard: 55,
        specs: {
          size: 'Mixed Cobble (2" - 6"+)',
          weightPerYard: '~3,000 lbs (1.5 tons)',
          compaction: 'None',
          coverage: '1 yard covers ~80 sq ft at 4" deep',
        },
        bestFor: [
          'Dry Creek Beds',
          'Water Features',
          'Drainage Swales',
          'Erosion Control',
        ],
        proTip:
          'Mix with smaller river rock or pea gravel to create a more natural, varied look in dry creek beds.',
      },
      {
        name: 'River Rock',
        slug: 'river-rock',
        description:
          "Naturally tumbled, smooth stones in a range of sizes (2–6 inches) and earthy color tones. Sourced from regional riverbeds, these stones add a polished, organic look to any landscape. Use them for dry creek beds, water features, border edging, ground cover around trees, and anywhere you want a natural, low-maintenance aesthetic that won't decompose or fade.",
        image:
          'https://cdn.magicpatterns.com/uploads/nqfErfP96HqjncDBfVrrmT/image.png',
        pricePerYard: 76,
        specs: {
          size: '2" - 6" Rounded',
          weightPerYard: '~3,000 lbs (1.5 tons)',
          compaction: 'None',
          coverage: '1 yard covers ~80 sq ft at 4" deep',
        },
        bestFor: [
          'Decorative Borders',
          'Around Trees',
          'Water Features',
          'Low-maintenance Ground Cover',
        ],
        proTip:
          'Lay heavy-duty landscape fabric underneath river rock to prevent weeds from growing up through the stones.',
      },
      {
        name: 'Lava Rock',
        slug: 'lava-rock',
        description:
          'Lightweight, porous volcanic rock in a deep reddish-brown color. Weighs roughly half as much as regular gravel, making it easy to spread and great for rooftop planters or areas with weight restrictions. The porous texture retains moisture and insulates plant roots. Popular for fire pit surrounds, succulent gardens, modern xeriscaping, and adding bold color contrast to landscape beds.',
        image:
          'https://cdn.magicpatterns.com/uploads/qsjdvgEGrJSuiRhGf62bek/image.png',
        pricePerYard: 84,
        specs: {
          size: '1" - 2" Porous',
          weightPerYard: '~1,500 lbs (0.75 tons)',
          compaction: 'None',
          coverage: '1 yard covers ~160 sq ft at 2" deep',
        },
        bestFor: [
          'Fire Pits',
          'Succulent Gardens',
          'Rooftop Planters',
          'Color Contrast',
        ],
        proTip:
          'Because it is so lightweight, avoid using lava rock in areas prone to heavy water flow or flooding, as it can wash away.',
      },
    ],
  },
  {
    name: 'Quarry Rock',
    description:
      'Large, angular quarry stone for heavy-duty applications. Too coarse to compact into a smooth surface, but unmatched for erosion control, rip-rap, structural fill, and rugged base layers where bulk and stability matter most.',
    image:
      'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&h=400&fit=crop',
    products: [
      {
        name: '2" Ballast',
        slug: '2-inch-ballast',
        description:
          'Uniformly sized, angular crushed stone at approximately 2 inches. Originally specified for railroad track beds, ballast rock is prized for its consistent sizing and excellent load distribution. Ideal for heavy-duty base layers under structures, stabilizing soft ground for equipment access, French drain systems that need high flow capacity, and as a clean structural fill. The uniform size creates predictable void space for drainage while providing solid bearing strength.',
        image:
          'https://cdn.magicpatterns.com/uploads/ho5P4d7utXxQj3BvApJKP3/2inch_clean.jpg',
        images: [
          'https://cdn.magicpatterns.com/uploads/ho5P4d7utXxQj3BvApJKP3/2inch_clean.jpg',
          'https://cdn.magicpatterns.com/uploads/ik7pRdL9kiSZvgVtnBt7NS/2inch_clean2dark.png',
        ],
        pricePerYard: 55,
        specs: {
          size: '2" Uniform Angular',
          weightPerYard: '~3,000 lbs (1.5 tons)',
          compaction: 'Good (Interlocking)',
          coverage: '1 yard covers ~80 sq ft at 4" deep',
        },
        bestFor: [
          'Heavy Base Layers',
          'Soft Ground Stabilization',
          'High-flow Drains',
          'Structural Fill',
        ],
        proTip:
          'Excellent for creating temporary construction entrances to keep mud off the street.',
      },
      {
        name: '2-4" Spalls',
        slug: '2-4-quarry-rock',
        description:
          'Large, angular quarry stone ranging from 2 to 4 inches. Too big to compact into a smooth surface, but excellent for erosion control on slopes and embankments, rip-rap along ditches and waterways, heavy-duty fill in wet areas, and as a rugged base layer for construction access roads. A tough, utilitarian rock for jobs that need serious bulk and stability.',
        image:
          'https://cdn.magicpatterns.com/uploads/4UjtVoq8sViWWuG31jax7Z/2-4_querry_rocks.png',
        images: [
          'https://cdn.magicpatterns.com/uploads/4UjtVoq8sViWWuG31jax7Z/2-4_querry_rocks.png',
          'https://cdn.magicpatterns.com/uploads/aUsbs8gj6VDBChPjU1Zum7/image.png',
          'https://cdn.magicpatterns.com/uploads/3Y3q3VVY27CmEuYorBt4P9/2_4_inch_rock.png',
        ],
        pricePerYard: 70,
        specs: {
          size: '2" - 4" Angular',
          weightPerYard: '~3,000 lbs (1.5 tons)',
          compaction: 'Interlocking',
          coverage: '1 yard covers ~50 sq ft at 6" deep',
        },
        bestFor: [
          'Erosion Control',
          'Ditch Lining',
          'Construction Entrances',
          'Heavy Fill',
        ],
        proTip:
          'The angular edges of quarry spalls lock together under weight, making them much more stable on slopes than rounded river rock.',
      },
      {
        name: '4-8" Spalls',
        slug: '4-8-quarry-spalls',
        description:
          'Extra-large angular quarry stone ranging from 4 to 8 inches. Built for the toughest jobs — heavy rip-rap along streams and shorelines, large-scale erosion control on steep embankments, structural backfill behind retaining walls, and armoring drainage outfalls. The substantial size and sharp edges lock together under their own weight, creating a durable, interlocking barrier that resists displacement from water and gravity.',
        image:
          'https://cdn.magicpatterns.com/uploads/hvSCmeUCjnrWbMHe6RxXCV/4-8_rocks_253gravel.png',
        pricePerYard: 70,
        specs: {
          size: '4" - 8" Angular',
          weightPerYard: '~3,000 lbs (1.5 tons)',
          compaction: 'Interlocking',
          coverage: 'Varies by application',
        },
        bestFor: [
          'Heavy Rip-rap',
          'Steep Embankments',
          'Retaining Wall Backfill',
          'Drainage Outfalls',
        ],
        proTip:
          'Use as the foundational layer in large retaining walls or gabion baskets where maximum stability and drainage are required.',
      },
      {
        name: 'Large Rocks',
        slug: 'large-rocks',
        description:
          'Massive 1-man to 4-man quarry rocks for heavy-duty erosion control, retaining features, and structural armoring. These oversized stones range from roughly 100 lbs up to 2,000+ lbs and are ideal for rip-rap on steep banks, shoreline protection, large retaining walls, and anywhere you need serious mass to hold ground. Call for availability and sizing.',
        image:
          'https://cdn.magicpatterns.com/uploads/k7oPHKfHaUE7huyVsdSJo6/large_rocks.png',
        pricePerYard: 125,
        specs: {
          size: '1-Man to 4-Man',
          weightPerYard: '~3,200 lbs (1.6 tons)',
          compaction: 'N/A',
          coverage: 'Varies by application',
        },
        bestFor: [
          'Shoreline Protection',
          'Large Retaining Walls',
          'Heavy Armoring',
          'Landscape Boulders',
        ],
        proTip:
          'Ensure you have the proper heavy equipment (excavator) on site to move and place these rocks safely.',
      },
    ],
  },
]
